import {AfterViewChecked, ChangeDetectorRef, Component, ViewContainerRef} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Login} from '../../../types/login';
import {LoggedUser} from '../../../types/loggedUser';
import {LocalStorageService} from '../../../../api/services/localStorage';
import {User} from '../../../types/user';
import {LoggedCompetitor} from '../../../types/loggedCompetitor';
import {Competitor} from '../../../types/competitor';
import {HttpHandlingService} from '../../../../api/services/http-handling.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormControl, Validators} from '@angular/forms';
import {CrewSample} from '../../../types/crew';
import {SERVER_API_URL} from '../../../types/app.constants';
import { ForgotPasswordDialogComponent } from './forgot-password-dialog/forgot-password-dialog.component';

@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html'
})
export class LoginDialogComponent implements AfterViewChecked{

  hide = true;
  login = '';
  password = '';
  rememberMe = false;

  loginFieldControl = new UntypedFormControl('', Validators.required);
  passwordFieldControl = new UntypedFormControl('', Validators.required);

  serverIsDown = false;

  constructor(private http: HttpClient, private localStorage: LocalStorageService, private httpHandlingService: HttpHandlingService,
              public dialogRef: MatDialogRef<LoginDialogComponent>, private cdr: ChangeDetectorRef, public dialog: MatDialog, private viewContainerRef: ViewContainerRef) {}

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  /**
   * function for authenticate user with database and starter for login process
   */
  onLoginUser(): void {
    const authenticateURL = SERVER_API_URL + '/api/authenticate';
    const login = new Login(this.login, this.password, this.rememberMe);
    this.http.post<any>(authenticateURL, login, {headers: this.httpHandlingService.simpleHeader()}).subscribe(data => {
      const token = data.id_token;
      let headers = new HttpHeaders({'Content-Type': 'application/json'});
      headers = headers.append('Authorization', 'Bearer ' +  token);
      this.getUserAndInsertDataToLocalStorage(headers, token);
    }, (error: HttpErrorResponse) => { this.handleLoginHttpErrors(error.status); });
  }

  /**
   * function for receiving user object from database and saving it into local storage
   * @param authorizationHeaders which contains information about authorization header
   * @param token received by backend as a JWT token
   */
  getUserAndInsertDataToLocalStorage(authorizationHeaders: HttpHeaders, token: string): void {
    const usersURL = SERVER_API_URL + '/api/users/' + this.login;
    this.http.get<User>(usersURL, {headers: authorizationHeaders}).subscribe((user: User) => {
      const loggedUser = new LoggedUser(user.id, this.login,
        token, user.langKey, this.addDayToCurrentDate(14), user.authorities);
      this.localStorage.set('loggedUser', loggedUser);
      this.getCompetitorAndInsertDataToLocalStorage(authorizationHeaders, loggedUser.id, user);
    });
  }

  /**
   * function for receiving competitor object from database and saving it into local storage
   * @param authorizationHeaders which contains information about authorization header
   * @param loggedUserId with information about current logged user, to retrieve competitor from database
   * @param userFromServer with information about user received from database
   */
  getCompetitorAndInsertDataToLocalStorage(authorizationHeaders: HttpHeaders, loggedUserId: number, userFromServer: User): void {
    const competitorURL = SERVER_API_URL + '/api/competitors/user/' + loggedUserId;
    this.http.get<Competitor>(competitorURL, {headers: authorizationHeaders}).subscribe((competitor: Competitor) => {
        if (competitor !== null) {
          const loggedCompetitor = new LoggedCompetitor(competitor.id, competitor.firstName, competitor.lastName,
            competitor.role, competitor.userId);
          this.localStorage.set('loggedCompetitor', loggedCompetitor);

          this.getCrewsByCompetitor(authorizationHeaders, loggedCompetitor);
        }
      },
      (error: HttpErrorResponse) => this.handleLoginHttpErrors(error.status));
  }

  /**
   * function for receiving crews related to competitor from database and saving it into local storage
   * @param authorizationHeaders which contains information about authorization header
   * @param loggedCompetitor with information about current logged competitor and related crews
   */
  getCrewsByCompetitor(authorizationHeaders: HttpHeaders, loggedCompetitor: LoggedCompetitor): void {
    const getCrewsByCompetitorURL = SERVER_API_URL + '/api/crews-active/' + loggedCompetitor.id;
    this.http.get<CrewSample[]>(getCrewsByCompetitorURL,
      {headers: authorizationHeaders}).subscribe((crews: CrewSample[]) => {
        this.localStorage.set('loggedCompetitorCrews', crews);
      },
      (error: HttpErrorResponse) => { this.handleLoginHttpErrors(error.status); },
      () => { this.dialogRef.close(); window.location.reload(); });
  }

  /**
   * function for evaluating, if all necessary fields are filled
   * this function disables button, if all data are not provided
   */
  allFieldsAreFilled(): boolean {
    return !(this.login !== '' && this.password !== '');
  }

  /**
   * function for setting 14 days of token expiration
   * @param days which provide length of token expiration in days
   */
  addDayToCurrentDate(days): Date {
    const currentDate = new Date();
    return new Date(currentDate.setDate(currentDate.getDate() + days));
  }

  /**
   * function for handling HTTP errors in the process of logging in
   */
  handleLoginHttpErrors(status: number): void {
    switch (status) {
      case 0: this.serverIsDown = true; break;
      case 404: this.dialogRef.close(); window.location.reload(); break; // if competitor card is not created
      default: {
        this.loginFieldControl.setErrors({unauthorized: true});
        this.passwordFieldControl.setErrors({unauthorized: true});
        this.serverIsDown = false;
        break;
      }
    }
  }

  /**
   * function which provides error messages in the process of logging in
   * @param fieldControl with information about desired field (login, password)
   */
  getErrorMessage(fieldControl: UntypedFormControl): string {
    return this.httpHandlingService.handleUserErrorOutput(fieldControl);
  }

  /**
   * function for checking, if user is manipulating with one of the inputs
   */
  onInputChangeCheck(): void {
    this.loginFieldControl.setErrors(null);
    this.passwordFieldControl.setErrors(null);
  }

  forgotPassword(): void {
    this.dialogRef.close();
    this.dialog.open(ForgotPasswordDialogComponent);
  }
}
